import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Music, Upload, Trash2, Loader2, Pencil, Check } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const EMPTY = { title: "", artist: "", year: "", theme: "", start: 0 };

function TrackForm({ value, onChange }) {
  const set = (k) => (e) => onChange({ ...value, [k]: k === "start" ? Number(e.target.value) || 0 : e.target.value });
  return (
    <div className="grid sm:grid-cols-5 gap-2">
      <Input className="sm:col-span-2" placeholder="Titre *" value={value.title} onChange={set("title")} data-testid="music-title-input" />
      <Input placeholder="Artiste" value={value.artist} onChange={set("artist")} data-testid="music-artist-input" />
      <Input placeholder="Thème (ex. Années 50)" value={value.theme} onChange={set("theme")} data-testid="music-theme-input" />
      <Input placeholder="Année" value={value.year} onChange={set("year")} data-testid="music-year-input" />
      <label className="sm:col-span-5 flex items-center gap-2 text-sm text-gray-600">
        Début de l'extrait du blind test (en secondes) :
        <Input type="number" min={0} className="w-24" value={value.start} onChange={set("start")} data-testid="music-start-input" />
      </label>
    </div>
  );
}

function TrackRow({ t, onSaved }) {
  const [edit, setEdit] = useState(null);
  const save = async (patch) => {
    try { await api.put(`/admin/music/${t.id}`, { ...t, ...patch }); setEdit(null); onSaved(); }
    catch (e) { toast.error(e.response?.data?.detail || "Enregistrement impossible."); }
  };
  const remove = async () => {
    if (!window.confirm(`Supprimer « ${t.title} » ?`)) return;
    await api.delete(`/admin/music/${t.id}`).catch(() => toast.error("Suppression impossible."));
    onSaved();
  };
  return (
    <div className="rounded-xl border border-gray-200 p-3 space-y-2" data-testid={`music-row-${t.id}`}>
      {edit ? <TrackForm value={edit} onChange={setEdit} /> : (
        <div className="flex flex-wrap items-center gap-3">
          <div className="min-w-0 flex-1">
            <p className="font-medium text-gray-800 truncate">{t.title}{t.artist && <span className="text-gray-500 font-normal"> · {t.artist}</span>}</p>
            <p className="text-xs text-gray-500">{[t.theme, t.year, t.start ? `extrait à ${t.start} s` : ""].filter(Boolean).join(" · ") || "Sans thème"}</p>
          </div>
          <audio controls preload="none" src={`${BACKEND}${t.audio_url}`} className="h-9 w-56" />
        </div>
      )}
      <div className="flex items-center gap-3">
        <label className="flex items-center gap-2 text-sm text-gray-600">
          <Switch checked={t.active} onCheckedChange={(v) => save({ ...(edit || {}), active: v })} data-testid={`music-active-${t.id}`} /> {t.active ? "Visible" : "Masquée"}
        </label>
        {edit ? (
          <>
            <Button size="sm" onClick={() => save(edit)} className="rounded-lg bg-emerald-700 hover:bg-emerald-800" data-testid={`music-save-${t.id}`}><Check className="w-4 h-4 mr-1" /> Enregistrer</Button>
            <Button size="sm" variant="ghost" onClick={() => setEdit(null)} data-testid={`music-cancel-${t.id}`}>Annuler</Button>
          </>
        ) : (
          <Button size="sm" variant="outline" className="rounded-lg" onClick={() => setEdit({ title: t.title, artist: t.artist, year: t.year, theme: t.theme, start: t.start })} data-testid={`music-edit-${t.id}`}><Pencil className="w-4 h-4" /></Button>
        )}
        <Button size="sm" variant="outline" className="rounded-lg text-red-600" onClick={remove} data-testid={`music-delete-${t.id}`}><Trash2 className="w-4 h-4" /></Button>
      </div>
    </div>
  );
}

// Bibliothèque musicale : blind test et chansons préférées des patients.
export default function MusicAdmin() {
  const [tracks, setTracks] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const input = useRef(null);
  const load = () => api.get("/admin/music").then((r) => setTracks(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const pick = (f) => {
    setFile(f);
    if (f && !form.title) setForm((v) => ({ ...v, title: f.name.replace(/\.[^.]+$/, "").replace(/[_-]+/g, " ") }));
  };
  const send = async () => {
    setBusy(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      fd.append("file", file);
      await api.post("/admin/music", fd);
      toast.success("Musique ajoutée");
      setForm(EMPTY); setFile(null); if (input.current) input.current.value = ""; load();
    } catch (e) { toast.error(e.response?.data?.detail || "Envoi impossible."); }
    finally { setBusy(false); }
  };
  return (
    <Card className="border-gray-200 mt-8" data-testid="music-admin">
      <CardContent className="p-6">
        <h3 className="font-semibold text-gray-800 mb-1 flex items-center gap-2"><Music className="w-4 h-4 text-rose-500" /> Musiques</h3>
        <p className="text-sm text-gray-500 mb-4">Vos fichiers servent au blind test et aux chansons préférées des patients. N'envoyez que des musiques que vous avez le droit de diffuser.</p>
        <div className="rounded-2xl border border-dashed border-gray-300 p-4 mb-5 space-y-3">
          <input ref={input} type="file" accept="audio/*" className="hidden" onChange={(e) => pick(e.target.files?.[0] || null)} data-testid="music-file-input" />
          <Button variant="outline" className="rounded-lg" disabled={busy} onClick={() => input.current?.click()} data-testid="music-pick-button">
            <Upload className="w-4 h-4 mr-2" /> {file ? file.name : "Choisir un fichier (MP3, M4A…)"}
          </Button>
          <TrackForm value={form} onChange={setForm} />
          <Button disabled={!file || !form.title.trim() || busy} onClick={send} className="rounded-lg bg-emerald-700 hover:bg-emerald-800" data-testid="music-upload-button">
            {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Ajouter la musique
          </Button>
        </div>
        <p className="text-sm text-gray-500 mb-2" data-testid="music-count">{tracks.length} musique(s) · le blind test demande au moins 3 musiques visibles.</p>
        <div className="space-y-3">{tracks.map((t) => <TrackRow key={t.id} t={t} onSaved={load} />)}</div>
      </CardContent>
    </Card>
  );
}
